import { useNavigate } from 'react-router-dom';

interface CulturePillar {
  title: string;
  description: string;
  icon: string;
  image: string;
  query: string;
  tags: string[];
}

const pillars: CulturePillar[] = [
  {
    title: 'Art & Artisanat',
    description:
      "Sculptures, poteries, batiks et tissages. Le savoir-faire des maîtres artisans, prêt à rejoindre votre intérieur.",
    icon: 'ri-palette-line',
    image:
      'https://readdy.ai/api/search-image?query=Vibrant%20African%20artisan%20market%20displaying%20handwoven%20baskets%20carved%20wooden%20sculptures%20and%20painted%20pottery%2C%20warm%20golden%20light%2C%20rich%20earthy%20terracotta%20tones%2C%20traditional%20craftsmanship%2C%20professional%20editorial%20photography%2C%20simple%20warm%20background&width=800&height=600&seq=culture-art&orientation=landscape',
    query: 'artisanat',
    tags: ['Sculpture', 'Batik', 'Poterie'],
  },
  {
    title: 'Musique & Danse',
    description:
      "Des djembés aux rythmes modernes, plongez dans une scène musicale vibrante et des artistes pleins de talent.",
    icon: 'ri-music-2-line',
    image:
      'https://readdy.ai/api/search-image?query=Traditional%20West%20African%20drummers%20performing%20at%20golden%20sunset%2C%20djembe%20drums%2C%20warm%20orange%20light%2C%20energetic%20dance%2C%20colorful%20traditional%20clothing%2C%20festive%20joyful%20atmosphere%2C%20professional%20photography%2C%20warm%20earthy%20background&width=800&height=600&seq=culture-music&orientation=landscape',
    query: 'musique',
    tags: ['Djembé', 'Concert', 'Festival'],
  },
  {
    title: 'Gastronomie',
    description:
      "Fufu, akoumé, sauce d'arachide, poisson braisé… Savourez une cuisine authentique qui raconte une histoire.",
    icon: 'ri-restaurant-2-line',
    image:
      'https://readdy.ai/api/search-image?query=Delicious%20West%20African%20dishes%20served%20in%20rustic%20clay%20bowls%2C%20fufu%20and%20peanut%20sauce%2C%20grilled%20fish%2C%20colorful%20fresh%20spices%2C%20warm%20ambient%20lighting%2C%20wooden%20table%2C%20professional%20food%20photography%2C%20rich%20warm%20tones%2C%20simple%20dark%20background&width=800&height=600&seq=culture-food&orientation=landscape',
    query: 'restaurant',
    tags: ['Maquis', 'Cuisine locale', 'Saveurs'],
  },
  {
    title: 'Mode & Wax',
    description:
      "Pagnes, tenues sur mesure et accessoires. Une mode qui mêle tradition et modernité, portée avec fierté.",
    icon: 'ri-t-shirt-line',
    image:
      'https://readdy.ai/api/search-image?query=Elegant%20African%20fashion%20model%20wearing%20vibrant%20wax%20print%20ankara%20dress%2C%20colorful%20patterns%2C%20warm%20studio%20lighting%2C%20gold%20jewelry%2C%20modern%20editorial%20photography%2C%20rich%20warm%20tones%2C%20clean%20simple%20background&width=800&height=600&seq=culture-fashion&orientation=landscape',
    query: 'mode',
    tags: ['Wax', 'Sur-mesure', 'Accessoires'],
  },
  {
    title: 'Traditions & Festivals',
    description:
      "Cérémonies, masques et danses ancestrales. Vivez l'âme des traditions qui font la fierté de nos peuples.",
    icon: 'ri-flag-2-line',
    image:
      'https://readdy.ai/api/search-image?query=Colorful%20traditional%20African%20festival%20celebration%2C%20dancers%20in%20vibrant%20ceremonial%20costumes%20and%20masks%2C%20warm%20golden%20hour%20light%2C%20joyful%20cultural%20heritage%2C%20professional%20photography%2C%20warm%20earthy%20tones&width=800&height=600&seq=culture-tradition&orientation=landscape',
    query: 'culture',
    tags: ['Cérémonie', 'Masques', 'Héritage'],
  },
  {
    title: 'Tourisme & Patrimoine',
    description:
      "Sites historiques, architectures traditionnelles et paysages à couper le souffle. Explorez un patrimoine unique.",
    icon: 'ri-landscape-line',
    image:
      'https://readdy.ai/api/search-image?query=Traditional%20African%20architecture%20heritage%20site%2C%20mud-brick%20buildings%20with%20geometric%20patterns%2C%20warm%20sunset%20light%2C%20palm%20trees%2C%20cultural%20tourism%2C%20professional%20travel%20photography%2C%20warm%20golden%20tones%2C%20simple%20background&width=800&height=600&seq=culture-tourism&orientation=landscape',
    query: 'tourisme',
    tags: ['Patrimoine', 'Sites', 'Découverte'],
  },
];

function CultureSection() {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
            Culture &amp; Patrimoine
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
            Célébrez la richesse culturelle africaine
          </h2>
          <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto">
            Art, musique, gastronomie, mode et traditions. Découvrez un patrimoine vivant et des
            créateurs qui portent l&apos;Afrique vers le monde.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {pillars.map((pillar) => (
            <button
              key={pillar.title}
              onClick={() => navigate(`/directory?q=${encodeURIComponent(pillar.query)}`)}
              className="group text-left rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-400/40 overflow-hidden transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent" />
                <div className="absolute top-3 left-3 w-11 h-11 flex items-center justify-center rounded-lg bg-background-50/90 backdrop-blur-sm border border-background-200/70">
                  <i className={`${pillar.icon} text-xl text-accent-400`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                  {pillar.title}
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  {pillar.description}
                </p>
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {pillar.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-accent-400 group-hover:gap-2.5 transition-all">
                  <span>Explorer</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-10 md:mt-12 text-center">
          <button
            onClick={() => navigate('/culture')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-foreground-950 font-semibold text-sm hover:bg-accent-400 transition-colors whitespace-nowrap"
          >
            Explorer la page Culture
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}

export default CultureSection;